import type { ToolConfig } from "../types";

export const rentTools: ToolConfig[] = [
  // 1. Rent Agreement Cost
  {
    slug: "rent-agreement-cost",
    category: "rent",
    title: "Rent Agreement Registration Cost Calculator",
    shortTitle: "Agreement Cost",
    description: "Estimate legal rent agreement drafting cost",
    keywords: ["rent", "agreement", "cost", "stamp", "notary", "legal"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
      { type: "number", key: "tenureYears", label: "Lease Duration", suffix: "years", min: 1, max: 10, step: 1, default: 2 },
      { type: "select", key: "state", label: "State", options: [
        { value: "maharashtra", label: "Maharashtra" },
        { value: "karnataka", label: "Karnataka" },
        { value: "tamil_nadu", label: "Tamil Nadu" },
        { value: "delhi", label: "Delhi" },
        { value: "punjab", label: "Punjab" },
      ], default: "maharashtra" },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const years = v.tenureYears as number;
      const state = v.state as string;

      const totalRentValue = rent * 12 * years;

      // Stamp duty varies by state (approx 0.5-2% of total rent)
      const stampDutyRates: Record<string, number> = {
        maharashtra: 0.01,
        karnataka: 0.01,
        tamil_nadu: 0.02,
        delhi: 0.01,
        punjab: 0.015,
      };
      const stampDutyRate = stampDutyRates[state] ?? 0.01;
      const stampDuty = totalRentValue * stampDutyRate;

      // Notary fees (fixed)
      const notaryFees = 500;

      // Legal/advocate fees for drafting
      const legalFees = 1500;

      const totalCost = stampDuty + notaryFees + legalFees;

      return {
        monthlyRent: Math.round(rent),
        tenureYears: years,
        totalRentValue: Math.round(totalRentValue),
        stampDuty: Math.round(stampDuty),
        notaryFees: Math.round(notaryFees),
        legalFees: Math.round(legalFees),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Agreement Registration Cost", format: "currency", isPrimary: true },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
      { key: "notaryFees", label: "Notary Charges", format: "currency" },
      { key: "legalFees", label: "Legal/Advocate Fees", format: "currency" },
      { key: "totalRentValue", label: "Total Rent Over Lease Period", format: "currency" },
    ],
    content: {
      whatIs: "Rent agreement registration requires stamp duty (tax on lease agreement), notary charges, and legal fees. Stamp duty varies by state (0.5-2% of total rent). Registration makes the agreement legally valid and protects both landlord and tenant.",
      faqs: [
        {
          question: "Is rent agreement registration mandatory?",
          answer: "Legally, it depends on state and local rules. In most cities, written agreement is standard but registration varies. Registered agreement provides legal protection; unregistered is riskier in disputes.",
        },
        {
          question: "Who pays for rent agreement registration?",
          answer: "Typically split or negotiated between landlord and tenant. Some agreements specify tenant pays; others split 50-50. Common practice: tenant bears cost of registration as part of taking the property.",
        },
        {
          question: "What's stamp duty on rent agreement?",
          answer: "Stamp duty is a tax on the lease document. Maharashtra: 0.5-1% of total rent value. Karnataka: 1%. Tamil Nadu: 2%. Delhi: 1%. It's paid to the state revenue department at time of registration.",
        },
        {
          question: "Can I avoid registering the agreement?",
          answer: "You can have unregistered agreement (just typed/handwritten), but it lacks legal weight. If dispute arises, registered agreement is stronger evidence. Also, registered agreement protects against tenant/landlord fraud.",
        },
        {
          question: "What if landlord and tenant disagree on clauses?",
          answer: "Both parties should agree on every clause before registration. Disputes after registration are harder to resolve. Draft agreement carefully with legal help; don't sign if uncomfortable with any term.",
        },
      ],
      relatedSlugs: ["rent/rent-vs-buy", "rent/rent-affordability", "rent/hra-calculator"],
    },
  },

  // 2. Rent Affordability
  {
    slug: "rent-affordability",
    category: "rent",
    title: "How Much Rent Can I Afford Calculator",
    shortTitle: "Rent Affordability",
    description: "Check your housing affordability ratio",
    keywords: ["rent", "afford", "income", "30%", "budget", "monthly"],
    inputs: [
      { type: "number", key: "monthlyIncome", label: "Monthly In-Hand Income", prefix: "₹", min: 10000, max: 1000000, step: 5000, default: 100000 },
      { type: "toggle", key: "use30Percent", label: "Use 30% Rule (Standard)?", default: true, helpText: "Max 30% of income to rent" },
      { type: "number", key: "customPercent", label: "Custom % (If Not 30%)", suffix: "%", min: 10, max: 50, step: 5, default: 30 },
    ],
    calculate(v) {
      const income = v.monthlyIncome as number;
      const use30 = v.use30Percent as boolean;
      const customPercent = v.customPercent as number;

      const percentToUse = use30 ? 30 : customPercent;
      const affordableRent = (income * percentToUse) / 100;
      const unaffordableAbove = affordableRent * 1.2; // 20% above recommended
      const emergencyBudget = (income * 20) / 100; // 20% for emergencies

      return {
        monthlyIncome: Math.round(income),
        affordableRent: Math.round(affordableRent),
        percentUsed: percentToUse,
        remainingAfterRent: Math.round(income - affordableRent),
        notRecommended: Math.round(unaffordableAbove),
        emergencyBudget: Math.round(emergencyBudget),
      };
    },
    resultFields: [
      { key: "affordableRent", label: "Max Recommended Rent", format: "currency", isPrimary: true },
      { key: "percentUsed", label: "% of Income", format: "percent" },
      { key: "remainingAfterRent", label: "Remaining After Rent", format: "currency" },
      { key: "notRecommended", label: "Not Recommended (20% Above)", format: "currency" },
      { key: "emergencyBudget", label: "Emergency Budget (20% of income)", format: "currency" },
    ],
    content: {
      whatIs: "The 30% rule states you should spend no more than 30% of income on rent. This leaves 20% for savings/emergency, 20% for utilities/transport, and 30% for food and other needs. If you exceed 30%, you risk financial stress.",
      faqs: [
        {
          question: "Why 30% rule for rent?",
          answer: "Financial experts recommend 30% because it balances housing security with financial flexibility. Above 30%, you're rent-burdened (struggle with other expenses). Below 20%, you're under-utilizing space/location advantages.",
        },
        {
          question: "What if I can't afford 30% rule in my city?",
          answer: "In expensive cities (Bangalore, Mumbai, Delhi), 30% might be impossible. Option 1: Increase income. Option 2: Share apartment (reduce per-person rent). Option 3: Move to cheaper locality. Don't exceed 40% without strong buffer.",
        },
        {
          question: "Should I include other housing costs in rent calculation?",
          answer: "The 30% rule is specifically for rent. Separately budget for: utilities (₹1-3K/month), maintenance charge (₹500-2K), internet (₹400-800). Total housing cost might be 35-40% of income.",
        },
        {
          question: "What if rent is very cheap in my area?",
          answer: "Enjoy it! Save the difference. No need to upgrade housing just because you can afford more. Build emergency fund (6 months expenses), invest, or increase quality of life in other ways.",
        },
        {
          question: "Is the 30% rule applicable to home loan EMI?",
          answer: "The rule applies to rent. For home loans, 40-50% of income on EMI is considered manageable (loan is wealth-building, not pure expense). Still, keep total housing cost under 50% of income.",
        },
      ],
      relatedSlugs: ["rent/rent-vs-buy", "rent/hra-calculator", "salary/take-home-salary-calculator"],
    },
  },

  // 4. Rent vs Buy
  {
    slug: "rent-vs-buy",
    category: "rent",
    title: "Rent vs Buy Comparison Over Years",
    shortTitle: "Rent vs Buy",
    description: "Compare renting vs buying a property",
    keywords: ["rent", "buy", "cost", "investment", "comparison", "home", "property"],
    inputs: [
      { type: "number", key: "propertyPrice", label: "Property Price (If Buying)", prefix: "₹", min: 0, max: 100000000, step: 100000, default: 5000000 },
      { type: "number", key: "monthlyRent", label: "Monthly Rent", prefix: "₹", min: 0, max: 500000, step: 5000, default: 50000 },
      { type: "number", key: "years", label: "Time Period to Compare", suffix: "years", min: 1, max: 30, step: 1, default: 10 },
      { type: "number", key: "propertyAppreciation", label: "Annual Property Appreciation %", suffix: "%", min: 0, max: 15, step: 0.5, default: 5 },
      { type: "number", key: "downPayment", label: "Down Payment (If Buying)", prefix: "₹", min: 0, max: 100000000, step: 100000, default: 1500000 },
    ],
    calculate(v) {
      const price = v.propertyPrice as number;
      const rent = v.monthlyRent as number;
      const years = v.years as number;
      const appreciation = v.propertyAppreciation as number;
      const down = v.downPayment as number;

      // Renting cost
      const totalRentCost = rent * 12 * years;

      // Buying cost
      const loanAmount = price - down;
      const emi = loanAmount > 0 ? (loanAmount * 0.09 * Math.pow(1.09, years * 12)) / (Math.pow(1.09, years * 12) - 1) : 0; // Rough 9% interest
      const totalEMI = emi * 12 * years;

      // Property value after appreciation
      let propertyValue = price;
      for (let i = 0; i < years; i++) {
        propertyValue = propertyValue * (1 + appreciation / 100);
      }

      // Net cost of buying
      const maintenanceAndTax = 30000 * years; // Approx ₹30K per year
      const netCostOfBuying = down + totalEMI + maintenanceAndTax - (propertyValue - price);

      const savingsOrCost = totalRentCost - netCostOfBuying;

      return {
        propertyPrice: Math.round(price),
        monthlyRent: Math.round(rent),
        years: years,
        totalRentCost: Math.round(totalRentCost),
        downPayment: Math.round(down),
        totalEMI: Math.round(totalEMI),
        maintenanceAndTax: Math.round(maintenanceAndTax),
        propertyValueAfter: Math.round(propertyValue),
        appreciation: Math.round(propertyValue - price),
        netCostOfBuying: Math.round(netCostOfBuying),
        difference: Math.round(savingsOrCost),
      };
    },
    resultFields: [
      { key: "difference", label: "Rent is Cheaper By / Buying Saves", format: "currency", isPrimary: true },
      { key: "totalRentCost", label: "Total Rent Cost", format: "currency" },
      { key: "netCostOfBuying", label: "Net Cost of Buying", format: "currency" },
      { key: "propertyValueAfter", label: "Property Value After Appreciation", format: "currency" },
      { key: "appreciation", label: "Total Appreciation Gain", format: "currency" },
    ],
    content: {
      whatIs: "Rent vs buy decision depends on: time horizon, location prices, interest rates, personal stability. Short-term (< 5 years): renting is cheaper. Long-term (10+ years): buying usually wins due to appreciation and equity buildup.",
      faqs: [
        {
          question: "When is buying better than renting?",
          answer: "Buying is better if: 1) You plan to stay 7+ years, 2) Property appreciates 5%+ annually, 3) Interest rates are reasonable (<8%), 4) You have 20-30% down payment, 5) Market is below peak prices.",
        },
        {
          question: "When is renting better than buying?",
          answer: "Renting is better if: 1) You're relocating within 3-5 years, 2) Property prices are inflated, 3) You need flexibility, 4) Rents are very cheap in your area, 5) You lack down payment or income stability.",
        },
        {
          question: "What about home loan interest tax deduction?",
          answer: "Under Section 24(b), you can deduct home loan interest (max ₹2L/year). This reduces taxable income and effective EMI burden. Factoring this in makes buying more attractive in higher tax brackets.",
        },
        {
          question: "Does rent increase matter?",
          answer: "Yes. If rent increases 8% annually but property appreciates only 5%, rent gets expensive. Factoring long-term rent increase makes buying more attractive over 15-20 years.",
        },
        {
          question: "Should I consider emotional factors?",
          answer: "Yes. Owning brings stability, customization, pride. Renting brings flexibility, lower stress. For some, these non-financial factors are worth the premium. This calculator focuses on finances only.",
        },
      ],
      relatedSlugs: ["rent/rental-yield-calculator", "property/home-loan-emi-calculator", "property/property-investment-calculator"],
    },
  },

  // 5. Rent Escalation
  {
    slug: "rent-escalation-calculator",
    category: "rent",
    title: "Rent Escalation Calculator",
    shortTitle: "Rent Escalation",
    description: "Project future rent costs with annual increases",
    keywords: ["rent", "escalation", "increase", "future", "projection", "annual"],
    inputs: [
      { type: "number", key: "currentRent", label: "Current Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
      { type: "number", key: "escalationPercent", label: "Annual Escalation %", suffix: "%", min: 0, max: 20, step: 0.5, default: 8 },
      { type: "number", key: "years", label: "Years to Project", suffix: "years", min: 1, max: 20, step: 1, default: 5 },
    ],
    calculate(v) {
      const rent = v.currentRent as number;
      const escalation = (v.escalationPercent as number) / 100;
      const years = v.years as number;

      let projectedRent = rent;
      let year1Rent = 0;
      let year3Rent = 0;
      let totalRentPaid = 0;

      for (let i = 1; i <= years; i++) {
        projectedRent = projectedRent * (1 + escalation);
        totalRentPaid += projectedRent * 12;
        if (i === 1) year1Rent = projectedRent;
        if (i === 3) year3Rent = projectedRent;
      }

      const totalIncrease = projectedRent - rent;
      const averageRent = totalRentPaid / (years * 12);

      return {
        currentRent: Math.round(rent),
        escalationPercent: v.escalationPercent as number,
        year1Rent: Math.round(year1Rent),
        year3Rent: Math.round(year3Rent),
        projectedRent: Math.round(projectedRent),
        totalIncrease: Math.round(totalIncrease),
        averageMonthlyRent: Math.round(averageRent),
        totalRentPaid: Math.round(totalRentPaid),
        years: years,
      };
    },
    resultFields: [
      { key: "projectedRent", label: `Projected Rent After ${5} Years`, format: "currency", isPrimary: true },
      { key: "year1Rent", label: "Year 1 Rent", format: "currency" },
      { key: "year3Rent", label: "Year 3 Rent", format: "currency" },
      { key: "totalIncrease", label: "Total Increase from Current", format: "currency" },
      { key: "averageMonthlyRent", label: "Average Monthly Rent Over Period", format: "currency" },
    ],
    content: {
      whatIs: "Landlords typically increase rent annually by 5-15% depending on location, inflation, and market demand. This calculator projects future rent costs, helping you budget and plan whether to buy or continue renting.",
      faqs: [
        {
          question: "What's a typical annual rent increase in India?",
          answer: "Usually 5-10% per year. Tier-1 cities (Mumbai, Delhi, Bangalore): 8-12%. Tier-2: 6-10%. Smaller cities: 5-8%. Increases are higher during inflation and lower during slowdowns. Rent freeze periods also occur.",
        },
        {
          question: "Is rent escalation tied to inflation?",
          answer: "Not directly, but inflation influences it. If inflation is 6%, landlord increases rent to maintain real purchasing power. High inflation can push escalation to 10-15%. Deflation might mean no increase or decrease.",
        },
        {
          question: "Can I negotiate rent escalation in the agreement?",
          answer: "Yes. You can agree to: fixed rent (no increase), capped increase (max 5% p.a.), indexed to inflation (CPI-based), or stepped increase (e.g., 5% in year 1-2, 7% in year 3-5). Negotiate before signing.",
        },
        {
          question: "How does rent escalation affect rent vs buy decision?",
          answer: "If rent escalation is 8-10% and property appreciates 5%, long-term renting becomes expensive. Buying lock you into fixed EMI (often lower than escalating rent), making it attractive over 10-15 years.",
        },
        {
          question: "What if I can't afford escalated rent?",
          answer: "Options: 1) Relocate to cheaper area, 2) Find roommate (share rent), 3) Buy property (lock-in price), 4) Negotiate with landlord for smaller increase, 5) Build financial cushion when you can afford current rent.",
        },
      ],
      relatedSlugs: ["rent/rent-affordability", "rent/rent-vs-buy", "rent/rent-to-income-ratio"],
    },
  },

  // 6. Security Deposit Interest
  {
    slug: "security-deposit-interest",
    category: "rent",
    title: "Security Deposit Interest Calculator",
    shortTitle: "Deposit Interest",
    description: "Calculate security deposit interest earned",
    keywords: ["deposit", "interest", "security", "rent", "savings", "earned"],
    inputs: [
      { type: "number", key: "depositAmount", label: "Security Deposit Amount", prefix: "₹", min: 0, max: 10000000, step: 10000, default: 200000 },
      { type: "number", key: "annualRate", label: "Interest Rate per Annum", suffix: "%", min: 0, max: 15, step: 0.5, default: 4 },
      { type: "number", key: "months", label: "Holding Period", suffix: "months", min: 1, max: 120, step: 1, default: 24 },
      { type: "radio", key: "compounding", label: "Interest Compounding", options: [
        { value: "simple", label: "Simple Interest" },
        { value: "compound", label: "Compound Interest (Monthly)" }
      ], default: "simple" },
    ],
    calculate(v) {
      const deposit = v.depositAmount as number;
      const rate = (v.annualRate as number) / 100;
      const months = v.months as number;
      const compound = v.compounding as string;

      let interest = 0;
      if (compound === "simple") {
        interest = (deposit * rate * months) / 12;
      } else {
        const monthlyRate = rate / 12;
        const finalAmount = deposit * Math.pow(1 + monthlyRate, months);
        interest = finalAmount - deposit;
      }

      const totalWithInterest = deposit + interest;
      const annualEquivalent = (interest / months) * 12;

      return {
        depositAmount: Math.round(deposit),
        annualRate: v.annualRate as number,
        holdingMonths: months,
        interest: Math.round(interest),
        totalWithInterest: Math.round(totalWithInterest),
        annualEquivalent: Math.round(annualEquivalent),
        compoundingType: compound === "simple" ? "Simple" : "Compound (Monthly)",
      };
    },
    resultFields: [
      { key: "interest", label: "Interest Earned", format: "currency", isPrimary: true },
      { key: "totalWithInterest", label: "Total (Deposit + Interest)", format: "currency" },
      { key: "annualEquivalent", label: "Annualized Interest Earning", format: "currency" },
      { key: "depositAmount", label: "Original Security Deposit", format: "currency" },
    ],
    content: {
      whatIs: "Security deposit (usually 2-3 months rent) is held by landlord as collateral. Some states/regulations require landlord to pay interest on deposit. Interest rates vary: 0-6% p.a. depending on state and regulations.",
      faqs: [
        {
          question: "Is landlord required to pay interest on security deposit?",
          answer: "Varies by state. Maharashtra, Tamil Nadu: Interest is mandatory (4-6% p.a.). Some states: optional. Check your state's rental agreement regulations. If mandatory, insist on it in the agreement.",
        },
        {
          question: "How much interest should I get?",
          answer: "Typically 4-6% p.a. depending on state. Some states specify the rate; others leave it negotiable. At 6%, a ₹2L deposit for 2 years earns ₹24K interest. Insist on written terms.",
        },
        {
          question: "When do I get the interest?",
          answer: "Usually when you vacate and deposit is returned. Some agreements allow annual interest payments. Clarify in the agreement. If landlord delays returning deposit, you lose interest (and can claim damages).",
        },
        {
          question: "Can interest reduce the deduction amount?",
          answer: "No. Interest is separate. If property is damaged (₹50K damage, deposit ₹2L), landlord deducts ₹50K and returns ₹1.5L + interest on ₹1.5L. Deductions are made first, interest on remainder.",
        },
        {
          question: "What if landlord doesn't pay interest?",
          answer: "You can file complaint with district consumer court or local rent tribunal. Get written proof of deposit (receipt) and amount. Courts typically order interest payment + penalties.",
        },
      ],
      relatedSlugs: ["rent/rent-agreement-cost", "investment/fd-calculator", "savings/savings-calculator"],
    },
  },

  // 7. HRA Calculator
  {
    slug: "hra-calculator",
    category: "rent",
    title: "HRA Exemption Calculator",
    shortTitle: "HRA Exemption",
    description: "Calculate HRA exemption under Income Tax Act",
    keywords: ["hra", "exemption", "tax", "rent", "allowance", "house"],
    inputs: [
      { type: "number", key: "basicPlusDa", label: "Basic Salary + DA", prefix: "₹", min: 0, max: 500000, step: 1000, default: 40000 },
      { type: "number", key: "hraReceived", label: "HRA Received", prefix: "₹", min: 0, max: 500000, step: 1000, default: 12000 },
      { type: "number", key: "rentPaid", label: "Actual Rent Paid", prefix: "₹", min: 0, max: 500000, step: 1000, default: 18000 },
      { type: "select", key: "city", label: "City Category", options: [
        { value: "metro", label: "Metro (Delhi, Mumbai, Bangalore, Chennai, Hyderabad, Kolkata, Pune, Ahmedabad)" },
        { value: "non_metro", label: "Non-Metro" }
      ], default: "metro" },
    ],
    calculate(v) {
      const basic = v.basicPlusDa as number;
      const hra = v.hraReceived as number;
      const rent = v.rentPaid as number;
      const city = v.city as string;

      const deductionPercent = city === "metro" ? 0.5 : 0.4;

      // Three conditions for HRA exemption
      const method1 = hra; // Full HRA received
      const method2 = rent - (basic * deductionPercent); // Rent - 50% of basic (metro) or 40% (non-metro)
      const method3 = basic * deductionPercent; // 50% of basic (metro) or 40% (non-metro)

      // Least of the three
      const exemption = Math.min(method1, Math.max(0, method2), method3);
      const taxableHra = Math.max(0, hra - exemption);

      return {
        basicPlusDa: Math.round(basic),
        hraReceived: Math.round(hra),
        rentPaid: Math.round(rent),
        cityCategory: city === "metro" ? "Metro (50%)" : "Non-Metro (40%)",
        exemptionAllowed: Math.round(exemption),
        taxableHra: Math.round(taxableHra),
        method1: Math.round(method1),
        method2: Math.round(Math.max(0, method2)),
        method3: Math.round(method3),
      };
    },
    resultFields: [
      { key: "exemptionAllowed", label: "HRA Exemption (Tax-Free)", format: "currency", isPrimary: true },
      { key: "taxableHra", label: "Taxable HRA", format: "currency" },
      { key: "hraReceived", label: "Total HRA Received", format: "currency" },
      { key: "cityCategory", label: "City Category", format: "text" },
      { key: "rentPaid", label: "Actual Rent Paid", format: "currency" },
    ],
    content: {
      whatIs: "HRA (House Rent Allowance) exemption under Section 10(13A) allows you to claim part of HRA as tax-free. Exemption is the least of three: HRA received, Rent - 50% basic (metro) / 40% basic (non-metro), or 50%/40% of basic. No rent receipt = no exemption.",
      faqs: [
        {
          question: "Do I need rent receipt to claim HRA exemption?",
          answer: "YES. Rent receipt from landlord (or registered agreement) is mandatory. Without proof of rent, no HRA exemption allowed. Ask landlord for formal rent receipt monthly.",
        },
        {
          question: "Why is HRA based on basic salary?",
          answer: "Income tax classifies salary components separately. HRA is linked to basic + DA because it represents the cost of living adjustment. Perks and bonuses don't count for HRA deduction.",
        },
        {
          question: "What if I pay rent but HRA is not received?",
          answer: "You get no exemption. HRA is a specific allowance; if your company doesn't give it, you can't claim HRA exemption (though you might get rent deduction under Section 10(13A) with employer authorization).",
        },
        {
          question: "What if rent paid is less than HRA received?",
          answer: "Exemption is limited to actual rent (minus 50% basic). Example: HRA ₹12K, rent ₹8K, basic ₹30K. Exemption = least of (12K, 8K - 15K=-0, 15K) = 0. Can't claim HRA beyond actual rent.",
        },
        {
          question: "Can landlord not give rent receipt to avoid tax?",
          answer: "Landlord's income tax is separate. You must get receipt for your tax exemption claim. If landlord refuses, get registered agreement and bank transfer proof. Don't skip HRA claim—huge tax savings.",
        },
      ],
      relatedSlugs: ["salary/take-home-salary-calculator", "rent/rent-agreement-cost", "rent/rent-to-income-ratio"],
    },
  },

  // 8. Rent to Income Ratio
  {
    slug: "rent-to-income-ratio",
    category: "rent",
    title: "Rent to Income Ratio Calculator",
    shortTitle: "Rent to Income",
    description: "Calculate rent to income ratio percentage",
    keywords: ["rent", "income", "ratio", "percentage", "30%", "affordability"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
      { type: "number", key: "monthlyIncome", label: "Monthly Income", prefix: "₹", min: 10000, max: 1000000, step: 5000, default: 100000 },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const income = v.monthlyIncome as number;

      const ratio = income > 0 ? (rent / income) * 100 : 0;
      const remaining = income - rent;
      const is30Percent = ratio <= 30;

      return {
        monthlyRent: Math.round(rent),
        monthlyIncome: Math.round(income),
        rentToIncomeRatio: Math.round(ratio * 10) / 10,
        remainingAfterRent: Math.round(remaining),
        isAffordable30Percent: is30Percent ? 1 : 0,
        warningIfHigh: ratio > 40 ? 1 : 0,
      };
    },
    resultFields: [
      { key: "rentToIncomeRatio", label: "Rent to Income Ratio %", format: "percent", isPrimary: true },
      { key: "monthlyRent", label: "Monthly Rent", format: "currency" },
      { key: "monthlyIncome", label: "Monthly Income", format: "currency" },
      { key: "remainingAfterRent", label: "Remaining Income After Rent", format: "currency" },
    ],
    content: {
      whatIs: "Rent-to-income ratio shows what % of income goes to rent. Below 30% is healthy (leaves room for savings and other expenses). 30-40% is tight. Above 40% is rent-burdened (risky). This ratio helps assess housing affordability.",
      faqs: [
        {
          question: "Why is 30% the standard ratio?",
          answer: "Financial advisors recommend: 30% rent, 20% savings, 20% utilities/transport, 30% food/other. Above 30%, you struggle with other expenses. Below 20%, you have excess that can be invested or saved.",
        },
        {
          question: "What if my ratio is 40%+?",
          answer: "Rent-burdened. Options: 1) Increase income (job change, side income), 2) Move to cheaper area, 3) Share rent (roommate), 4) Buy property if possible (fixed EMI). Don't sustain >40% long-term.",
        },
        {
          question: "Should I include other housing costs in ratio?",
          answer: "This ratio is just rent. Total housing cost (rent + utilities + maintenance) might be 35-45% of income. The 30% rule is specifically for rent, leaving headroom for other costs.",
        },
        {
          question: "How does rent escalation affect this ratio?",
          answer: "Rent increase reduces remaining income. If rent increases 8% annually but salary 5%, ratio creeps up. After 5 years: 25% ratio becomes 28%. This is why rent escalation matters in long-term planning.",
        },
        {
          question: "Is ratio important for loan applications?",
          answer: "Yes. Banks assess rent-to-income for loan approval. High rent ratio (40%+) signals financial stress; you might not qualify for large loans. Ideally keep below 30% for borrowing capacity.",
        },
      ],
      relatedSlugs: ["rent/rent-affordability", "rent/rent-escalation-calculator", "rent/rent-vs-buy"],
    },
  },

  // 9. Commercial Rent Calculator
  {
    slug: "commercial-rent-calculator",
    category: "rent",
    title: "Commercial Space Rent Per Sqft Calculator",
    shortTitle: "Commercial Rent",
    description: "Calculate commercial space rent cost",
    keywords: ["commercial", "rent", "office", "sqft", "space", "business"],
    inputs: [
      { type: "number", key: "rentPerSqft", label: "Rent Per Sq.Ft. Per Month", prefix: "₹", min: 10, max: 500, step: 5, default: 100 },
      { type: "number", key: "spaceSqft", label: "Space Size", suffix: "sq.ft.", min: 100, max: 100000, step: 100, default: 2000 },
      { type: "number", key: "months", label: "Lease Duration", suffix: "months", min: 12, max: 360, step: 12, default: 60 },
    ],
    calculate(v) {
      const perSqft = v.rentPerSqft as number;
      const sqft = v.spaceSqft as number;
      const months = v.months as number;

      const monthlyRent = perSqft * sqft;
      const annualRent = monthlyRent * 12;
      const totalRent = monthlyRent * months;

      const rentPerSqftAnnual = perSqft * 12;

      return {
        rentPerSqft: Math.round(perSqft),
        spaceSqft: Math.round(sqft),
        monthlyRent: Math.round(monthlyRent),
        annualRent: Math.round(annualRent),
        rentPerSqftAnnual: Math.round(rentPerSqftAnnual),
        leaseMonths: months,
        leaseYears: months / 12,
        totalRent: Math.round(totalRent),
      };
    },
    resultFields: [
      { key: "monthlyRent", label: "Monthly Rent", format: "currency", isPrimary: true },
      { key: "annualRent", label: "Annual Rent", format: "currency" },
      { key: "totalRent", label: `Total Rent for ${5} Years`, format: "currency" },
      { key: "spaceSqft", label: "Space Size", format: "number" },
      { key: "rentPerSqftAnnual", label: "Annual Rent Per Sq.Ft.", format: "currency" },
    ],
    content: {
      whatIs: "Commercial rent is typically quoted per square foot per month. This calculator helps you calculate total rent based on space size, rate, and lease duration. Commercial rates vary by location, amenities, and demand.",
      faqs: [
        {
          question: "What's typical commercial rent per sqft in Indian cities?",
          answer: "Central Business Districts: ₹150-400/sqft/month. Prime commercial: ₹100-200/sqft. Secondary commercial: ₹50-100/sqft. Tier-2 cities: ₹20-60/sqft. Rates depend on foot traffic, accessibility, building quality.",
        },
        {
          question: "Does commercial rent include maintenance?",
          answer: "No. Quoted rent is usually just the space. Separately, you pay: maintenance charges (₹5-20/sqft), property tax, utilities, insurance. Total cost = rent + 20-30% for other charges.",
        },
        {
          question: "How long are commercial leases?",
          answer: "Typically 3-5 years (shorter than residential). Can be renewed. Early termination clauses vary. Longer leases (5 years) might get discounts (5-10%). Always negotiate terms.",
        },
        {
          question: "Can commercial rent be negotiated?",
          answer: "Yes, especially for long leases or good tenants. Landlords offer discounts for upfront payment, lease duration, or fit-out provisions. First 6 months free, or 10-20% discount on committed 5-year lease is common.",
        },
        {
          question: "What's a reasonable lease break clause?",
          answer: "After 3 years, you should be able to exit with 1-2 months notice (or rent). Don't lock yourself without break clause—business needs change. Negotiate termination conditions upfront.",
        },
      ],
      relatedSlugs: ["business/company-registration-cost", "business/working-capital-calculator"],
    },
  },

  // 10-15: Remaining rent tools
  {
    slug: "lease-renewal-cost",
    category: "rent",
    title: "Lease Renewal Cost Calculator",
    shortTitle: "Lease Renewal Cost",
    description: "Calculate lease renewal cost and fees",
    keywords: ["lease", "renewal", "cost", "registration", "stamp", "legal"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "New Monthly Rent After Renewal", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 60000 },
      { type: "number", key: "renewalYears", label: "Renewal Period", suffix: "years", min: 1, max: 10, step: 1, default: 2 },
      { type: "number", key: "registrationCost", label: "Registration & Stamp Duty", prefix: "₹", min: 0, max: 100000, step: 1000, default: 5000 },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const years = v.renewalYears as number;
      const regCost = v.registrationCost as number;

      const totalNewRent = rent * 12 * years;
      const legalFees = 1000;
      const totalRenewalCost = regCost + legalFees;

      return {
        newMonthlyRent: Math.round(rent),
        renewalYears: years,
        registrationCost: Math.round(regCost),
        legalFees: Math.round(legalFees),
        totalRenewalCost: Math.round(totalRenewalCost),
        totalNewRentValue: Math.round(totalNewRent),
      };
    },
    resultFields: [
      { key: "totalRenewalCost", label: "Total Renewal Cost", format: "currency", isPrimary: true },
      { key: "registrationCost", label: "Registration & Stamp Duty", format: "currency" },
      { key: "legalFees", label: "Legal Fees", format: "currency" },
      { key: "newMonthlyRent", label: "New Monthly Rent", format: "currency" },
      { key: "totalNewRentValue", label: "Total Rent Over Renewal Period", format: "currency" },
    ],
    content: {
      whatIs: "When you renew a lease, you typically need to register the renewal deed (especially if rent changed). Costs include stamp duty and legal fees similar to initial agreement. Budget ₹3-10K for renewal.",
      faqs: [
        {
          question: "Do I need to renew the lease formally?",
          answer: "Yes, if rent changed or lease expired. Renewal deed registration protects both parties legally. Without renewal, you're technically not renting formally (risky for tenant and landlord).",
        },
        {
          question: "Can I renew without registration?",
          answer: "Possible but not recommended. Unregistered renewal lacks legal backing. If dispute arises, you have weak protection. Spend ₹3-5K on registration for legal safety.",
        },
        {
          question: "What if landlord wants to increase rent significantly?",
          answer: "Negotiate. Market norms: 10-20% increase over 2-3 years. 50% increase is unreasonable unless market exploded. Research comparable rents; have backup options to negotiate strength.",
        },
      ],
      relatedSlugs: ["rent/rent-agreement-cost", "rent/rent-escalation-calculator"],
    },
  },

  // Simplified remaining tools (11-15)
  {
    slug: "rent-increase-calculator",
    category: "rent",
    title: "Rent Increase Percentage Calculator",
    shortTitle: "Rent Increase %",
    description: "Calculate rent increase percentage hike",
    keywords: ["rent", "increase", "percentage", "change", "old", "new"],
    inputs: [
      { type: "number", key: "oldRent", label: "Old Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 40000 },
      { type: "number", key: "newRent", label: "New Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
    ],
    calculate(v) {
      const old = v.oldRent as number;
      const newRent = v.newRent as number;
      const difference = newRent - old;
      const increasePercent = old > 0 ? (difference / old) * 100 : 0;

      return {
        oldRent: Math.round(old),
        newRent: Math.round(newRent),
        difference: Math.round(difference),
        increasePercent: Math.round(increasePercent * 10) / 10,
      };
    },
    resultFields: [
      { key: "increasePercent", label: "Rent Increase %", format: "percent", isPrimary: true },
      { key: "newRent", label: "New Monthly Rent", format: "currency" },
      { key: "oldRent", label: "Old Monthly Rent", format: "currency" },
      { key: "difference", label: "Increase Amount", format: "currency" },
    ],
    content: {
      whatIs: "Calculate the percentage increase from old to new rent to assess if the increase is reasonable or negotiate better terms.",
      faqs: [
        {
          question: "Is a 20% rent increase reasonable?",
          answer: "Over 2 years, 20% = ~10% annually, which is standard. Over 1 year, 20% is high. Negotiate for phased increase or cap at 8-10%. Much depends on location and market conditions.",
        },
      ],
      relatedSlugs: ["rent/rent-escalation-calculator", "rent/rent-affordability"],
    },
  },

  {
    slug: "rent-per-sqft-calculator",
    category: "rent",
    title: "Rent Per Square Foot Calculator",
    shortTitle: "Rent Per Sq.Ft.",
    description: "Calculate rent per square foot pricing",
    keywords: ["rent", "sqft", "square", "foot", "per", "rate"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
      { type: "number", key: "builtupArea", label: "Built-up Area", suffix: "sq.ft.", min: 100, max: 10000, step: 100, default: 1200 },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const area = v.builtupArea as number;

      const rentPerSqft = area > 0 ? rent / area : 0;
      const annualRentPerSqft = rentPerSqft * 12;

      return {
        monthlyRent: Math.round(rent),
        builtupArea: Math.round(area),
        rentPerSqftMonthly: Math.round(rentPerSqft * 100) / 100,
        rentPerSqftAnnual: Math.round(annualRentPerSqft * 100) / 100,
      };
    },
    resultFields: [
      { key: "rentPerSqftMonthly", label: "Rent Per Sq.Ft. Per Month", format: "currency", isPrimary: true },
      { key: "rentPerSqftAnnual", label: "Rent Per Sq.Ft. Per Year", format: "currency" },
      { key: "monthlyRent", label: "Total Monthly Rent", format: "currency" },
      { key: "builtupArea", label: "Built-up Area", format: "number" },
    ],
    content: {
      whatIs: "Rent per square foot is a standard metric to compare properties across sizes and locations. Helps identify if a property is overpriced or reasonably priced for the area.",
      faqs: [
        {
          question: "What's reasonable rent per sqft in residential?",
          answer: "Bangalore CBD: ₹40-80/sqft. Tier-2 cities: ₹20-50/sqft. Suburbs: ₹15-30/sqft. Smaller cities: ₹10-20/sqft. Variation based on location, age, amenities. Compare within same locality.",
        },
      ],
      relatedSlugs: ["rent/commercial-rent-calculator", "rent/rent-affordability"],
    },
  },

  {
    slug: "maintenance-charge-calculator",
    category: "rent",
    title: "Society Maintenance Charge Calculator",
    shortTitle: "Maintenance Charge",
    description: "Calculate maintenance charge calculator",
    keywords: ["maintenance", "charge", "society", "apartment", "cost", "monthly"],
    inputs: [
      { type: "number", key: "builtupArea", label: "Built-up Area of Apartment", suffix: "sq.ft.", min: 100, max: 10000, step: 100, default: 1200 },
      { type: "number", key: "chargePerSqft", label: "Maintenance Charge Per Sq.Ft.", prefix: "₹", min: 1, max: 50, step: 0.5, default: 3 },
    ],
    calculate(v) {
      const area = v.builtupArea as number;
      const chargeRate = v.chargePerSqft as number;

      const monthlyCharge = area * chargeRate;
      const annualCharge = monthlyCharge * 12;

      return {
        builtupArea: Math.round(area),
        chargePerSqft: chargeRate,
        monthlyCharge: Math.round(monthlyCharge),
        annualCharge: Math.round(annualCharge),
      };
    },
    resultFields: [
      { key: "monthlyCharge", label: "Monthly Maintenance Charge", format: "currency", isPrimary: true },
      { key: "annualCharge", label: "Annual Maintenance", format: "currency" },
      { key: "builtupArea", label: "Apartment Size", format: "number" },
    ],
    content: {
      whatIs: "Maintenance charge (or society fees) covers building upkeep: common areas, security, water, power. Typically ₹1-5/sqft/month depending on city and amenities. Budget this in housing costs.",
      faqs: [
        {
          question: "What's included in maintenance charge?",
          answer: "Common areas (lobby, corridors, gym), security, water supply, power backup, lift maintenance, waste management. Doesn't include your apartment repairs.",
        },
        {
          question: "Can I negotiate maintenance charge?",
          answer: "Limited. Determined by society's actual costs. You can negotiate if buying; for rent, it's usually included or separate (tenant's responsibility). Check if landlord or tenant pays.",
        },
      ],
      relatedSlugs: ["rent/rent-affordability", "property/home-maintenance-cost"],
    },
  },

  {
    slug: "paying-guest-cost",
    category: "rent",
    title: "PG vs Apartment Rent Comparison",
    shortTitle: "PG vs Apartment",
    description: "Compare paying guest accommodation cost",
    keywords: ["pg", "apartment", "comparison", "cost", "rent", "shared"],
    inputs: [
      { type: "number", key: "pgCost", label: "PG Monthly Cost (Incl. Food, WiFi)", prefix: "₹", min: 5000, max: 50000, step: 1000, default: 15000 },
      { type: "number", key: "apartmentRent", label: "Apartment Monthly Rent", prefix: "₹", min: 5000, max: 200000, step: 1000, default: 25000 },
      { type: "number", key: "roommates", label: "Roommates (If Sharing Apartment)", suffix: "people", min: 1, max: 4, step: 1, default: 2 },
    ],
    calculate(v) {
      const pg = v.pgCost as number;
      const apt = v.apartmentRent as number;
      const share = v.roommates as number;

      const perPersonApartment = apt / share;
      const savingsWithApartment = pg - perPersonApartment;
      const savingsAnnual = savingsWithApartment * 12;

      return {
        pgCost: Math.round(pg),
        apartmentRent: Math.round(apt),
        perPersonCost: Math.round(perPersonApartment),
        roommates: share,
        monthlySavings: Math.round(savingsWithApartment),
        annualSavings: Math.round(savingsAnnual),
        apartmentIsCheaper: savingsWithApartment > 0 ? 1 : 0,
      };
    },
    resultFields: [
      { key: "monthlySavings", label: "Monthly Savings (Apartment vs PG)", format: "currency", isPrimary: true },
      { key: "annualSavings", label: "Annual Savings", format: "currency" },
      { key: "pgCost", label: "PG Monthly Cost", format: "currency" },
      { key: "perPersonCost", label: "Apartment Per-Person Cost", format: "currency" },
    ],
    content: {
      whatIs: "PGs are convenient but sometimes pricey. Apartment with roommates can be cheaper. Compare: PG includes meals/WiFi (no additional cost), but apartment is shared responsibility. Quality and flexibility differ.",
      faqs: [
        {
          question: "Is PG cheaper than apartment?",
          answer: "Not always. Single PG: ₹12-18K. Apartment (₹25K split by 2) = ₹12.5K per person + utilities. Often similar cost. Apartment is cheaper if you find good roommates and split utilities smartly.",
        },
        {
          question: "Advantages of PG vs apartment?",
          answer: "PG: Less hassle, meals included, community, no landlord disputes. Apartment: More freedom, privacy, better amenities, cheaper per person, builds community feel. Choose based on lifestyle.",
        },
      ],
      relatedSlugs: ["rent/rent-affordability", "rent/rent-vs-buy"],
    },
  },

  {
    slug: "rent-receipt-total",
    category: "rent",
    title: "Annual Rent Receipt Total (Tax Purposes)",
    shortTitle: "Annual Rent Total",
    description: "Calculate annual rent receipt total",
    keywords: ["rent", "receipt", "annual", "total", "tax", "documentation"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "Monthly Rent", prefix: "₹", min: 5000, max: 500000, step: 1000, default: 50000 },
      { type: "number", key: "months", label: "Number of Months Renting", suffix: "months", min: 1, max: 12, step: 1, default: 12 },
      { type: "number", key: "rentIncrease", label: "Mid-Year Rent Increase (if any)", prefix: "₹", min: 0, max: 100000, step: 1000, default: 0 },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const months = v.months as number;
      const increase = v.rentIncrease as number;

      const totalBasicRent = rent * months;
      const totalIncrease = increase * (months - 6); // Assume increase from 7th month
      const totalAnnualRent = totalBasicRent + totalIncrease;
      const averageMonthly = totalAnnualRent / months;

      return {
        monthlyRent: Math.round(rent),
        months: months,
        totalBasicRent: Math.round(totalBasicRent),
        rentIncreaseAmount: Math.round(increase),
        totalAfterIncrease: Math.round(totalAnnualRent),
        averageMonthlyRent: Math.round(averageMonthly),
      };
    },
    resultFields: [
      { key: "totalAfterIncrease", label: "Total Annual Rent Paid", format: "currency", isPrimary: true },
      { key: "averageMonthlyRent", label: "Average Monthly Rent", format: "currency" },
      { key: "totalBasicRent", label: "Basic Rent (Before Increase)", format: "currency" },
      { key: "rentIncreaseAmount", label: "Rent Increase", format: "currency" },
    ],
    content: {
      whatIs: "Calculate total annual rent for income tax filing and HRA exemption claims. Collect rent receipts (monthly from landlord) to support documentation. Total annual rent is used in ITR and HRA calculation.",
      faqs: [
        {
          question: "What's needed to claim HRA in ITR?",
          answer: "Total annual rent paid (from receipts), HRA received annually, address proof showing rent address. All rent receipts (12 months) mandatory. Without receipts, HRA claim denied.",
        },
        {
          question: "How is HRA exemption calculated for tax?",
          answer: "Least of: HRA received, Actual rent - 50% of (basic + DA), or 50% of (basic + DA). Total annual figures. If monthly amounts vary (rent increase), calculate annual totals for both rent and HRA.",
        },
      ],
      relatedSlugs: ["rent/hra-calculator", "rent/rent-agreement-cost"],
    },
  },
];
